import React, { useState, useRef } from 'react';
import { uploadPlannerDocument, fetchEquipmentList, fetchPlannerDocument, fetchFileFromBlob, inactivatePlannerDocument } from './api';
import { message, Modal } from 'antd';
import JSZip from 'jszip';

// ── Light Theme Tokens ───────────────────────────────────────────────────────
const C = {
  bg: '#f4f6fb',
  surface: '#ffffff',
  surfaceHover: '#f0f3fa',
  border: '#e2e7f0',
  accent: '#3b5bdb',
  accentLight: 'rgba(59,91,219,0.08)',
  success: '#0f9b6e',
  successBg: 'rgba(15,155,110,0.08)',
  error: '#d63b3b',
  errorBg: 'rgba(214,59,59,0.08)',
  warn: '#c47d0a',
  warnBg: 'rgba(196,125,10,0.08)',
  text: '#1a1f2e',
  textMuted: '#5a6478',
  textFaint: '#9aa0b0',
  divider: '#edf0f7',
};

const card = {
  background: C.surface,
  borderRadius: '14px',
  border: `1px solid ${C.border}`,
  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
};

// ── Helper: extract blob filename from doc_url ────────────────────────────────
// Handles ?blobName=FILE.ext query param OR plain path segments
const getBlobName = (doc) => {
  const url = doc.pdf_url || '';  // ← was doc.doc_url
  return url? url.split('/').pop() : '' ;
};

const getExt = (filename) => filename.split('.').pop()?.toLowerCase() || 'file';

// ─────────────────────────────────────────────────────────────────────────────
// LEFT PANEL — Upload
// ─────────────────────────────────────────────────────────────────────────────
const UploadPanel = () => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadList, setUploadList] = useState([]);
  const fileInputRef = useRef(null);

  const validateFilename = (filename) => {
    const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
    const parts = nameWithoutExt.split('_');
    return parts.length >= 2 && parts[0] && parts[1];
  };

  const processFiles = (files) => {
    const entries = Array.from(files).map((file) => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      name: file.name,
      status: 'pending',
      message: '',
    }));
    setUploadList((prev) => [...prev, ...entries]);
    entries.forEach((entry) => uploadFile(entry));
  };

  const uploadFile = async (entry) => {
    if (!validateFilename(entry.name)) {
      setUploadList((prev) => prev.map((item) =>
        item.id === entry.id
          ? { ...item, status: 'error', message: 'Invalid filename. Expected: <EQUIPMENTcode>_<BATCHnumber>.pdf' }
          : item
      ));
      message.error(`${entry.name}: Invalid filename format`);
      return;
    }

    setUploadList((prev) => prev.map((item) =>
      item.id === entry.id ? { ...item, status: 'uploading', message: 'Uploading…' } : item
    ));

    try {
      const result = await uploadPlannerDocument(entry.file);
      setUploadList((prev) => prev.map((item) =>
        item.id === entry.id
          ? { ...item, status: 'success', message: result.message || 'Uploaded successfully' }
          : item
      ));
      message.success(`${entry.name}: Uploaded successfully`);
    } catch (error) {
      setUploadList((prev) => prev.map((item) =>
        item.id === entry.id
          ? { ...item, status: 'error', message: error.message || 'Upload failed' }
          : item
      ));
      message.error(`${entry.name}: ${error.message || 'Upload failed'}`);
    }
  };

  const statusMeta = (s) => ({
    color:  s === 'success' ? C.success : s === 'error' ? C.error : s === 'uploading' ? C.accent : C.textFaint,
    bg:     s === 'success' ? C.successBg : s === 'error' ? C.errorBg : s === 'uploading' ? C.accentLight : C.bg,
    icon:   s === 'success' ? '✓' : s === 'error' ? '✕' : s === 'uploading' ? '↑' : '·',
    label:  s === 'uploading' ? 'Uploading' : s === 'success' ? 'Done' : s === 'error' ? 'Failed' : 'Pending',
  });

  const counts = uploadList.reduce((acc, i) => {
    acc[i.status] = (acc[i.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: C.accentLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>📤</div>
        <div>
          <div style={{ fontSize: '15px', fontWeight: '700', color: C.text }}>Upload Documents</div>
          <div style={{ fontSize: '12px', color: C.textMuted,display:'flex',flexDirection:'column',gap:'4px' }}>
            Name files as 
            <code style={{ background: C.bg, padding: '1px 5px', borderRadius: '4px', fontSize: '11px', color: C.accent }}>{"<EQUIPMENTcode>_<BATCHnumber>_<descriptionOptional>.pdf"}</code>
            <code style={{ background: C.bg, padding: '1px 5px', borderRadius: '4px', fontSize: '11px', color: C.accent }}>Ex:PRE201_CATBC25002</code>
            <code style={{ background: C.bg, padding: '1px 5px', borderRadius: '4px', fontSize: '11px', color: C.accent }}>Ex:PRE201_CATBC25002_audit 1</code>
            <code style={{ background: C.bg, padding: '1px 5px', borderRadius: '4px', fontSize: '11px', color: C.accent }}>Ex:PRE201_CATBC25002_audit_1</code>  
          </div>
        </div>
      </div>

      {/* Drop Zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
        onDrop={(e) => { e.preventDefault(); setIsDragging(false); if (e.dataTransfer.files.length) processFiles(e.dataTransfer.files); }}
        onClick={() => fileInputRef.current.click()}
        style={{
          border: `2px dashed ${isDragging ? C.accent : C.border}`,
          borderRadius: '12px', padding: '36px 20px', textAlign: 'center',
          cursor: 'pointer', background: isDragging ? C.accentLight : C.bg,
          transition: 'all 0.2s', flexShrink: 0,
        }}
      >
        <div style={{ fontSize: '32px', marginBottom: '10px', opacity: isDragging ? 1 : 0.7 }}>
          {isDragging ? '📂' : '📁'}
        </div>
        <div style={{ fontSize: '14px', fontWeight: '600', color: isDragging ? C.accent : C.text, marginBottom: '4px' }}>
          {isDragging ? 'Release to upload' : 'Drag & drop files here'}
        </div>
        <div style={{ fontSize: '12px', color: C.textMuted }}>
          or <span style={{ color: C.accent, fontWeight: '600', textDecoration: 'underline' }}>browse files</span>
        </div>
        <input ref={fileInputRef} type="file" multiple onChange={(e) => { if (e.target.files.length) { processFiles(e.target.files); e.target.value = ''; } }} style={{ display: 'none' }} />
      </div>

      {/* Stats bar */}
      {uploadList.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: C.bg, borderRadius: '8px', border: `1px solid ${C.border}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: '12px' }}>
            {[['success', '✓', C.success], ['error', '✕', C.error], ['uploading', '↑', C.accent]].map(([s, icon, col]) =>
              counts[s] ? <span key={s} style={{ fontSize: '12px', color: col, fontWeight: '600' }}>{icon} {counts[s]}</span> : null
            )}
            <span style={{ fontSize: '12px', color: C.textMuted }}>{uploadList.length} total</span>
          </div>
          <button onClick={() => setUploadList([])} style={{ fontSize: '12px', color: C.textMuted, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
            Clear all
          </button>
        </div>
      )}

      {/* File list */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', minHeight: 0 }}>
        {uploadList.length === 0 ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center', color: C.textFaint }}>
              <div style={{ fontSize: '28px', marginBottom: '8px', opacity: 0.4 }}>📄</div>
              <div style={{ fontSize: '13px' }}>No files yet</div>
            </div>
          </div>
        ) : uploadList.map((item) => {
          const m = statusMeta(item.status);
          return (
            <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '8px', background: m.bg, border: `1px solid ${m.color}22` }}>
              <div style={{ width: '26px', height: '26px', borderRadius: '6px', background: m.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', color: m.color, fontWeight: '700', flexShrink: 0 }}>
                {item.status === 'uploading' ? <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>↻</span> : m.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: '500', color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                <div style={{ fontSize: '11px', color: m.color, marginTop: '1px' }}>{item.message || m.label}</div>
              </div>
              <button onClick={() => setUploadList((prev) => prev.filter((x) => x.id !== item.id))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textFaint, fontSize: '14px', flexShrink: 0 }}>✕</button>
            </div>
          );
        })}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// RIGHT PANEL — Fetch & Download
// ─────────────────────────────────────────────────────────────────────────────
const FetchPanel = () => {
  const [equipmentList, setEquipmentList] = useState([]);
  const [equipLoading, setEquipLoading]   = useState(false);
  const [equipLoaded, setEquipLoaded]     = useState(false);

  const [selectedEqp, setSelectedEqp]   = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm]     = useState('');

  const [docs, setDocs]               = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState(new Set()); // Set of pdf_urls for cross-page selection
  const [downloading, setDownloading]   = useState(false);
  const [deleting, setDeleting]         = useState(false);

  // Document search state
  const [docSearchTerm, setDocSearchTerm] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [5, 10, 20, 50];

  const dropdownRef = useRef(null);

  // ── Load equipment list on first open ─────────────────────────────────────
  const openDropdown = async () => {
    setDropdownOpen(true);
    if (!equipLoaded) {
      setEquipLoading(true);
      try {
        const { data } = await fetchEquipmentList();
        setEquipmentList(data);
        setEquipLoaded(true);
      } catch (e) {
        message.error('Failed to load equipment list');
      } finally {
        setEquipLoading(false);
      }
    }
  };

  // ── Select equipment → GET ?eqp_code=X&alleqpdocs=true ───────────────────
const selectEquipment = async (eqp) => {
  setSelectedEqp(eqp);
  setDropdownOpen(false);
  setSearchTerm('');
  setDocs([]);
  setSelectedDocs(new Set());
  setDocSearchTerm('');
  setCurrentPage(1); // Reset pagination
  setDocsLoading(true);

  try {
    const res = await fetchPlannerDocument(eqp.equipment_id, null, true);

    const records = Array.isArray(res) ? res : (res?.data ?? []);

    // ✅ FILTER ONLY RECORDS WITH PDF
    const docsWithPdf = records.filter(
      (doc) => doc.pdf_url && doc.pdf_url.trim() !== ''
    );

    setDocs(docsWithPdf);

  } catch (e) {
    message.error('Failed to fetch documents for equipment');
  } finally {
    setDocsLoading(false);
  }
};

  // Filter docs by search term (matches batch number or filename)
  const filteredDocs = docSearchTerm.trim()
    ? docs.filter((doc) => {
        const term = docSearchTerm.toLowerCase();
        const batch = (doc.product_batch_no || '').toLowerCase();
        const filename = getBlobName(doc).toLowerCase();
        // Split search into words for intelligent multi-word matching
        const words = term.split(/\s+/).filter(Boolean);
        return words.every((w) => batch.includes(w) || filename.includes(w));
      })
    : docs;

  // Pagination calculations
  const totalPages = Math.ceil(filteredDocs.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedDocs = filteredDocs.slice(startIndex, endIndex);

  // Get unique key for a doc (use pdf_url as identifier)
  const getDocKey = (doc) => doc.pdf_url;

  const toggleDoc = (doc) => {
    const key = getDocKey(doc);
    setSelectedDocs((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // Toggle all on current page
  const toggleAllCurrentPage = () => {
    const currentPageKeys = paginatedDocs.map(getDocKey);
    const allCurrentSelected = currentPageKeys.every(key => selectedDocs.has(key));
    
    setSelectedDocs((prev) => {
      const next = new Set(prev);
      if (allCurrentSelected) {
        currentPageKeys.forEach(key => next.delete(key));
      } else {
        currentPageKeys.forEach(key => next.add(key));
      }
      return next;
    });
  };

  // Select/Deselect all filtered documents across all pages
  const toggleAll = () => {
    const filteredKeys = new Set(filteredDocs.map(getDocKey));
    const allFilteredSelected = filteredDocs.length > 0 && filteredDocs.every(doc => selectedDocs.has(getDocKey(doc)));
    if (allFilteredSelected) {
      // Deselect only the filtered docs, keep others
      setSelectedDocs((prev) => {
        const next = new Set(prev);
        filteredKeys.forEach(key => next.delete(key));
        return next;
      });
    } else {
      // Select all filtered docs (add to existing selection)
      setSelectedDocs((prev) => {
        const next = new Set(prev);
        filteredKeys.forEach(key => next.add(key));
        return next;
      });
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page
  };

  // ── Download: single file or ZIP ──────────────────────────────────────────
  const handleDownload = async () => {
    const toDownload = docs.filter(doc => selectedDocs.has(getDocKey(doc)));
    if (!toDownload.length) return;

    setDownloading(true);
    try {
      if (toDownload.length === 1) {
        const blobName = getBlobName(toDownload[0]);
        const blob = await fetchFileFromBlob(blobName);
        const url = URL.createObjectURL(blob);
        Object.assign(document.createElement('a'), { href: url, download: blobName }).click();
        URL.revokeObjectURL(url);
        message.success('File downloaded');
      } else {
        const zip = new JSZip();
        await Promise.all(
          toDownload.map(async (doc) => {
            const blobName = getBlobName(doc);
            const blob = await fetchFileFromBlob(blobName);
            zip.file(blobName, blob);
          })
        );
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const url = URL.createObjectURL(zipBlob);
        Object.assign(document.createElement('a'), {
          href: url,
          download: `${selectedEqp?.equipment_name || 'documents'}_AT_Reports.zip`,
        }).click();
        URL.revokeObjectURL(url);
        message.success(`${toDownload.length} files downloaded as ZIP`);
      }
    } catch (e) {
      message.error(`Download failed: ${e.message}`);
    } finally {
      setDownloading(false);
    }
  };

  // ── Delete: mark selected documents as inactive ───────────────────────────
  const handleDelete = async () => {
    const toDelete = docs.filter(doc => selectedDocs.has(getDocKey(doc)));
    if (!toDelete.length) return;

    Modal.confirm({
      title: 'Delete Documents',
      content: `Are you sure you want to delete ${toDelete.length} document${toDelete.length > 1 ? 's' : ''}? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      centered: true,
      onOk: async () => {
        setDeleting(true);
        let successCount = 0;
        let failCount = 0;
        
        try {
          await Promise.all(
            toDelete.map(async (doc) => {
              try {
                await inactivatePlannerDocument(doc.id);
                successCount++;
              } catch (e) {
                failCount++;
                console.error(`Failed to delete document ${doc.id}:`, e);
              }
            })
          );

          // Remove deleted docs from state
          const deletedKeys = new Set(toDelete.map(getDocKey));
          setDocs((prev) => prev.filter((doc) => !deletedKeys.has(getDocKey(doc))));
          setSelectedDocs(new Set());

          // Adjust pagination if needed
          const remainingDocs = docs.filter((doc) => !deletedKeys.has(getDocKey(doc)));
          const newTotalPages = Math.ceil(remainingDocs.length / pageSize);
          if (currentPage > newTotalPages && newTotalPages > 0) {
            setCurrentPage(newTotalPages);
          } else if (newTotalPages === 0) {
            setCurrentPage(1);
          }

          if (failCount === 0) {
            message.success(`${successCount} document${successCount > 1 ? 's' : ''} deleted successfully`);
          } else {
            message.warning(`${successCount} deleted, ${failCount} failed`);
          }
        } catch (e) {
          message.error(`Delete failed: ${e.message}`);
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  const filteredEquipment = equipmentList.filter((e) =>
    e.equipment_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.equipment_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Check selections for current page
  const currentPageKeys = paginatedDocs.map(getDocKey);
  const allCurrentPageSelected = paginatedDocs.length > 0 && currentPageKeys.every(key => selectedDocs.has(key));
  const someCurrentPageSelected = currentPageKeys.some(key => selectedDocs.has(key)) && !allCurrentPageSelected;
  
  // Check selections for all filtered docs
  const allFilteredSelected = filteredDocs.length > 0 && filteredDocs.every(doc => selectedDocs.has(getDocKey(doc)));

  const extColors = { pdf: '#e74c3c', docx: '#2980b9', doc: '#2980b9', xlsx: '#27ae60', xls: '#27ae60' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(15,155,110,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>📥</div>
        <div>
          <div style={{ fontSize: '15px', fontWeight: '700', color: C.text }}>Fetch & Download</div>
          <div style={{ fontSize: '12px', color: C.textMuted }}>Select equipment to browse AT reports</div>
        </div>
      </div>

      {/* Custom Equipment Dropdown */}
      <div style={{ position: 'relative', flexShrink: 0 }} ref={dropdownRef}>
        <div
          onClick={dropdownOpen ? () => setDropdownOpen(false) : openDropdown}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 14px', borderRadius: '10px',
            border: `1.5px solid ${dropdownOpen ? C.accent : C.border}`,
            background: C.surface, cursor: 'pointer', transition: 'all 0.15s',
            boxShadow: dropdownOpen ? `0 0 0 3px ${C.accentLight}` : 'none',
          }}
        >
          <span style={{ fontSize: '14px', color: selectedEqp ? C.text : C.textFaint, fontWeight: selectedEqp ? '500' : '400' }}>
            {selectedEqp ? `${selectedEqp.equipment_name} — ${selectedEqp.equipment_id}` : 'Select equipment…'}
          </span>
          <span style={{ color: C.textFaint, fontSize: '12px', display: 'inline-block', transition: 'transform 0.2s', transform: dropdownOpen ? 'rotate(180deg)' : 'none' }}>▾</span>
        </div>

        {dropdownOpen && (
          <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, background: C.surface, borderRadius: '10px', border: `1.5px solid ${C.border}`, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 100, overflow: 'hidden' }}>
            <div style={{ padding: '8px 10px', borderBottom: `1px solid ${C.divider}` }}>
              <input
                autoFocus
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search equipment…"
                style={{ width: '100%', padding: '7px 10px', borderRadius: '7px', border: `1px solid ${C.border}`, fontSize: '13px', background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {equipLoading ? (
                <div style={{ padding: '20px', textAlign: 'center', color: C.textMuted, fontSize: '13px' }}>Loading…</div>
              ) : filteredEquipment.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: C.textFaint, fontSize: '13px' }}>No equipment found</div>
              ) : filteredEquipment.map((eqp) => (
                <div
                  key={eqp.id}
                  onClick={() => selectEquipment(eqp)}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 14px', cursor: 'pointer', fontSize: '13px', color: C.text, background: selectedEqp?.id === eqp.id ? C.accentLight : 'transparent', transition: 'background 0.1s' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = C.surfaceHover}
                  onMouseLeave={(e) => e.currentTarget.style.background = selectedEqp?.id === eqp.id ? C.accentLight : 'transparent'}
                >
                  <span style={{ fontWeight: '500' }}>{eqp.equipment_name}</span>
                  <span style={{ color: C.textMuted, fontSize: '12px' }}>{eqp.equipment_id}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Doc list area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>

        {/* Document search */}
        {docs.length > 0 && (
          <div style={{ position: 'relative', marginBottom: '10px', flexShrink: 0 }}>
            <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', color: C.textMuted, pointerEvents: 'none' }}>🔍</div>
            <input
              value={docSearchTerm}
              onChange={(e) => { setDocSearchTerm(e.target.value); setCurrentPage(1); }}
              placeholder="Search by batch number or filename…"
              style={{
                width: '100%', padding: '9px 12px 9px 34px', borderRadius: '9px',
                border: `1.5px solid ${docSearchTerm ? C.accent : C.border}`,
                fontSize: '13px', background: C.surface, color: C.text, outline: 'none',
                boxSizing: 'border-box', transition: 'border-color 0.15s',
              }}
            />
            {docSearchTerm && (
              <button
                onClick={() => { setDocSearchTerm(''); setCurrentPage(1); }}
                style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: C.textFaint, fontSize: '14px', padding: '2px' }}
              >
                ✕
              </button>
            )}
          </div>
        )}

        {/* Toolbar */}
        {docs.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '10px', flexShrink: 0 }}>
            {/* Selection controls row */}
            <div className="at-toolbar-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={allCurrentPageSelected}
                    ref={(el) => { if (el) el.indeterminate = someCurrentPageSelected; }}
                    onChange={toggleAllCurrentPage}
                    style={{ width: '15px', height: '15px', accentColor: C.accent, cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '12px', color: C.textMuted }}>Page</span>
                </label>
                <button
                  onClick={toggleAll}
                  style={{
                    padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600',
                    background: allFilteredSelected ? C.accentLight : C.bg,
                    color: allFilteredSelected ? C.accent : C.textMuted,
                    border: `1px solid ${allFilteredSelected ? C.accent : C.border}`,
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                >
                  {allFilteredSelected
                    ? (docSearchTerm ? 'Deselect All Matches' : 'Deselect Across All Pages')
                    : (docSearchTerm ? 'Select All Matches' : 'Select Across All Pages')}
                </button>
              </div>
              
              <span style={{ fontSize: '12px', color: selectedDocs.size > 0 ? C.accent : C.textMuted, fontWeight: selectedDocs.size > 0 ? '600' : '400' }}>
                {selectedDocs.size > 0
                  ? `${selectedDocs.size} of ${docs.length} selected${docSearchTerm ? ` (${filteredDocs.length} shown)` : ''}`
                  : `${docSearchTerm ? `${filteredDocs.length} of ${docs.length}` : docs.length} document${(docSearchTerm ? filteredDocs.length : docs.length) !== 1 ? 's' : ''}`}
              </span>

              <div className="at-toolbar-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {selectedDocs.size > 0 && (
                  <button
                    onClick={() => setSelectedDocs(new Set())}
                    disabled={deleting || downloading}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '7px 14px', borderRadius: '8px',
                      background: C.bg, color: C.textMuted,
                      border: `1.5px solid ${C.border}`,
                      fontSize: '13px', fontWeight: '600',
                      cursor: deleting || downloading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    ✕ Clear
                  </button>
                )}

                <button
                  onClick={handleDelete}
                  disabled={selectedDocs.size === 0 || deleting || downloading}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '7px 14px', borderRadius: '8px',
                    background: selectedDocs.size > 0 && !deleting && !downloading ? C.errorBg : C.bg,
                    color: selectedDocs.size > 0 && !deleting && !downloading ? C.error : C.textFaint,
                    border: `1.5px solid ${selectedDocs.size > 0 && !deleting && !downloading ? C.error : C.border}`,
                    fontSize: '13px', fontWeight: '600',
                    cursor: selectedDocs.size > 0 && !deleting && !downloading ? 'pointer' : 'not-allowed',
                    transition: 'all 0.15s', opacity: deleting ? 0.75 : 1,
                  }}
                >
                  {deleting
                    ? <><span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>↻</span> Deleting…</>
                    : <>🗑 Delete</>}
                </button>

                <button
                  onClick={handleDownload}
                  disabled={selectedDocs.size === 0 || downloading || deleting}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '7px 14px', borderRadius: '8px',
                    background: selectedDocs.size > 0 && !downloading && !deleting ? C.accent : C.bg,
                    color: selectedDocs.size > 0 && !downloading && !deleting ? '#fff' : C.textFaint,
                    border: `1.5px solid ${selectedDocs.size > 0 && !downloading && !deleting ? C.accent : C.border}`,
                    fontSize: '13px', fontWeight: '600',
                    cursor: selectedDocs.size > 0 && !downloading && !deleting ? 'pointer' : 'not-allowed',
                    transition: 'all 0.15s', opacity: downloading ? 0.75 : 1,
                  }}
                >
                  {downloading
                    ? <><span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>↻</span> Preparing…</>
                    : <>{selectedDocs.size > 1 ? '⬇ Download ZIP' : '⬇ Download'}</>}
                </button>
              </div>
            </div>

            {/* Pagination controls row */}
            <div className="at-pagination-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: C.bg, borderRadius: '8px', border: `1px solid ${C.border}`, flexWrap: 'wrap', gap: '8px' }}>
              {/* Page size selector */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', color: C.textMuted }}>Show:</span>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  style={{
                    padding: '4px 8px', borderRadius: '6px', border: `1px solid ${C.border}`,
                    background: C.surface, fontSize: '12px', color: C.text, cursor: 'pointer', outline: 'none',
                  }}
                >
                  {pageSizeOptions.map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
                <span style={{ fontSize: '12px', color: C.textMuted }}>per page</span>
              </div>

              {/* Page navigation */}
              <div className="at-pagination-nav" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '600',
                    background: currentPage === 1 ? C.bg : C.surface,
                    color: currentPage === 1 ? C.textFaint : C.text,
                    border: `1px solid ${C.border}`, cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  }}
                >
                  ««
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '600',
                    background: currentPage === 1 ? C.bg : C.surface,
                    color: currentPage === 1 ? C.textFaint : C.text,
                    border: `1px solid ${C.border}`, cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  }}
                >
                  ‹
                </button>
                
                <span style={{ fontSize: '12px', color: C.text, padding: '0 8px', minWidth: '80px', textAlign: 'center' }}>
                  Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                </span>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '600',
                    background: currentPage === totalPages ? C.bg : C.surface,
                    color: currentPage === totalPages ? C.textFaint : C.text,
                    border: `1px solid ${C.border}`, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  }}
                >
                  ›
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '600',
                    background: currentPage === totalPages ? C.bg : C.surface,
                    color: currentPage === totalPages ? C.textFaint : C.text,
                    border: `1px solid ${C.border}`, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  }}
                >
                  »»
                </button>
              </div>

              {/* Items range indicator */}
              <span className="at-pagination-range" style={{ fontSize: '11px', color: C.textMuted }}>
                {startIndex + 1}–{Math.min(endIndex, filteredDocs.length)} of {filteredDocs.length}
              </span>
            </div>
          </div>
        )}

        {/* List body */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, maxHeight: '400px' }}>
          {docsLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[1, 2, 3].map((i) => (
                <div key={i} style={{ height: '52px', borderRadius: '8px', background: `linear-gradient(90deg, ${C.bg} 25%, ${C.divider} 50%, ${C.bg} 75%)`, backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
              ))}
            </div>

          ) : !selectedEqp ? (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ textAlign: 'center', color: C.textFaint }}>
                <div style={{ fontSize: '32px', marginBottom: '10px', opacity: 0.35 }}>🔍</div>
                <div style={{ fontSize: '13px', fontWeight: '500' }}>Select equipment above</div>
                <div style={{ fontSize: '12px', marginTop: '4px' }}>Documents will appear here</div>
              </div>
            </div>

          ) : docs.length === 0 ? (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ textAlign: 'center', color: C.textFaint }}>
                <div style={{ fontSize: '32px', marginBottom: '10px', opacity: 0.35 }}>📭</div>
                <div style={{ fontSize: '13px', fontWeight: '500' }}>No documents found</div>
                <div style={{ fontSize: '12px', marginTop: '4px' }}>for {selectedEqp.equipment_name}</div>
              </div>
            </div>

          ) : filteredDocs.length === 0 ? (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ textAlign: 'center', color: C.textFaint }}>
                <div style={{ fontSize: '32px', marginBottom: '10px', opacity: 0.35 }}>🔎</div>
                <div style={{ fontSize: '13px', fontWeight: '500' }}>No matches for "{docSearchTerm}"</div>
                <div style={{ fontSize: '12px', marginTop: '4px' }}>Try different search terms</div>
              </div>
            </div>

          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
              {paginatedDocs.map((doc, idx) => {
                const docKey = getDocKey(doc);
                const isSelected = selectedDocs.has(docKey);
                const filename   = getBlobName(doc);
                const ext        = getExt(filename);
                const extColor   = extColors[ext] || C.textMuted;

                return (
                  <div
                    key={docKey || idx}
                    onClick={() => toggleDoc(doc)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      padding: '10px 12px', borderRadius: '9px',
                      border: `1.5px solid ${isSelected ? C.accent : C.border}`,
                      background: isSelected ? C.accentLight : C.surface,
                      cursor: 'pointer', transition: 'all 0.15s',
                    }}
                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = C.surfaceHover; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = isSelected ? C.accentLight : C.surface; }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleDoc(doc)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ width: '15px', height: '15px', accentColor: C.accent, cursor: 'pointer', flexShrink: 0 }}
                    />

                    {/* File type badge */}
                    <div style={{ width: '34px', height: '34px', borderRadius: '7px', background: extColor + '15', border: `1px solid ${extColor}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: '800', color: extColor, flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                      {ext}
                    </div>

                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: C.text, marginBottom: '2px' }}>
                        {doc.product_batch_no}
                      </div>
                      <div style={{ fontSize: '11px', color: C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {filename}
                      </div>
                    </div>

                    {/* Selected checkmark */}
                    {isSelected && (
                      <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '11px', flexShrink: 0, fontWeight: '700' }}>✓</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin    { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
      `}</style>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ROOT
// ─────────────────────────────────────────────────────────────────────────────
const ATReport = () => (
  <div className="at-report-root" style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '32px 24px', boxSizing: 'border-box' }}>
    <div style={{ width: '100%' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '20px', fontWeight: '800', color: C.text, margin: 0, letterSpacing: '-0.4px' }}>AT Report Manager</h1>
        <p style={{ fontSize: '13px', color: C.textMuted, margin: '4px 0 0 0' }}>Upload documents or browse and download existing AT reports by equipment</p>
      </div>

      <div className="at-report-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1px 1fr', gap: 0, minHeight: '600px' }}>
        <div className="at-report-panel" style={{ ...card, padding: '24px', marginRight: '12px' }}><UploadPanel /></div>
        <div className="at-report-divider" style={{ background: C.border }} />
        <div className="at-report-panel" style={{ ...card, padding: '24px', marginLeft: '12px' }}><FetchPanel /></div>
      </div>
    </div>

    <style>{`
      @media (max-width: 768px) {
        .at-report-root { padding: 16px 10px !important; }
        .at-report-grid {
          grid-template-columns: 1fr !important;
          min-height: auto !important;
        }
        .at-report-divider {
          display: none;
        }
        .at-report-panel {
          margin: 0 0 16px 0 !important;
          padding: 16px !important;
        }
        .at-toolbar-row {
          flex-direction: column !important;
          align-items: stretch !important;
        }
        .at-toolbar-actions {
          flex-wrap: wrap !important;
          justify-content: flex-start !important;
        }
        .at-pagination-row {
          flex-direction: column !important;
          align-items: stretch !important;
          gap: 8px !important;
        }
        .at-pagination-nav {
          justify-content: center !important;
        }
        .at-pagination-range {
          text-align: center !important;
        }
      }
    `}</style>
  </div>
);

export default ATReport;